import store from '../redux/store';
import { logout } from '../redux/action/authSlice';
import toast from 'react-hot-toast';

const baseURL = process.env.REACT_APP_BACKEND_URL || "https://apistore.iqbaldeveloper.my.id";

/**
 * A custom fetch wrapper that mimics Axios behavior.
 * This removes the need for the `axios` dependency which crashes 
 * on React Webpack 5 due to missing Node.js core polyfills.
 */
const customFetch = async (endpoint, options = {}) => {
  const url = `${baseURL}${endpoint}`;
  
  const state = store.getState();
  const token = state.auth?.token || localStorage.getItem('auth_token');
  
  const headers = new Headers({
    'Accept': 'application/json',
    ...(options.headers || {})
  });

  if (!options.isFormData) {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  } else {
    // For FormData, browser must auto-set Content-Type with boundary
    headers.delete('Content-Type');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const config = {
    ...options,
    headers,
    // NOTE: We do NOT use credentials:'include' because we use Bearer token auth.
    // 'include' requires backend CORS to specify exact origins (not wildcard '*'),
    // which causes issues especially during Google OAuth redirects.
  };

  const response = await fetch(url, config);

  if (response.status === 401) {
    store.dispatch(logout());
    toast.error('Session expired. Please log in again.');
    if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
      window.location.href = '/login';
    }
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await response.json() : null;

  if (!response.ok) {
    let errorMsg = data?.message || 'API request failed';

    // Extract first validation error if present (Laravel specific)
    if (response.status === 422 && data?.errors) {
      const firstError = Object.values(data.errors)[0]?.[0];
      if (firstError) {
        errorMsg = firstError;
      }
    }

    const error = new Error(errorMsg);
    // Mimic axios err.response.data shape to prevent rewriting our components
    error.response = { 
      data: { 
        ...(data || {}), 
        message: errorMsg // Override message so components pick up the specific validation error
      }, 
      status: response.status 
    };
    throw error;
  }

  // Mimic axios success res.data
  return { data };
};

const api = {
  get: (endpoint, options) => customFetch(endpoint, { ...options, method: 'GET' }),
  post: (endpoint, body, options = {}) => {
    const isFormData = body instanceof FormData;
    return customFetch(endpoint, { 
      ...options, 
      method: 'POST', 
      body: isFormData ? body : JSON.stringify(body),
      isFormData
    });
  },
  put: (endpoint, body, options = {}) => {
    const isFormData = body instanceof FormData;
    return customFetch(endpoint, { 
      ...options, 
      method: 'PUT', 
      body: isFormData ? body : JSON.stringify(body),
      isFormData
    });
  },
  delete: (endpoint, options) => customFetch(endpoint, { ...options, method: 'DELETE' }),
};

export default api;
